import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PillLink } from '@/components/generals/pill-link'
import { SectionLabel } from '@/components/generals/section-label'
import { EnquiryForm } from '@/components/contact/enquiry-form'

import {
    clientAreaLabel,
    company,
    phonesByCity
} from '@/constants/site'

const Contact: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contato'
                description="Fale com a Ativa Comércio Exterior. Agende uma conversa com nossos especialistas em desembaraço aduaneiro, assessoria em importação e exportação, logística internacional e consultoria."
            />

            <section
                data-header-theme='dark'
                className='relative isolate overflow-hidden bg-ink pb-20 pt-44 text-white md:pb-28 lg:pt-60'
            >
                <div aria-hidden='true' className='starfield absolute inset-0 -z-10 opacity-50' />
                <div
                    aria-hidden='true'
                    className='absolute inset-x-0 bottom-0 -z-10 h-full bg-[radial-gradient(60%_55%_at_80%_100%,rgba(20,130,220,0.4),transparent_75%)]'
                />

                <Container>
                    <SectionLabel tone='dark'>Contato</SectionLabel>

                    <h1 className='mt-6 max-w-4xl font-heading text-[2.2rem] uppercase leading-[1.02] tracking-[-0.02em] md:text-6xl lg:text-7xl'>
                        Agende uma conversa com nossos especialistas.
                    </h1>

                    <p className='mt-8 max-w-xl text-lg leading-relaxed text-white/70'>
                        Conheça nossos serviços sem compromisso. Estamos prontos para atendê-lo e superar suas expectativas.
                    </p>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <PillLink href={company.simulationUrl} variant='highlight'>
                            Faça uma simulação
                        </PillLink>

                        <PillLink href={company.whatsappUrl} variant='outline-light'>
                            Fale pelo WhatsApp
                        </PillLink>
                    </div>
                </Container>
            </section>

            <section id='formulario' data-header-theme='light' className='bg-white py-20 md:py-28'>
                <Container className='grid gap-16 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <SectionLabel>Formulário de contato</SectionLabel>

                        <h2 className='mt-5 font-heading text-2xl uppercase tracking-[-0.01em] md:text-3xl'>
                            Comece agora
                        </h2>

                        <div className='mt-10'>
                            <EnquiryForm />
                        </div>
                    </div>

                    <aside aria-label='Dados de contato' className='lg:col-span-4 lg:col-start-9'>
                        <div className='rounded-3xl bg-secondary p-8 lg:sticky lg:top-28'>
                            <h2 className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                Sede
                            </h2>

                            <address className='mt-4 text-lg not-italic leading-relaxed'>
                                {company.address.street}
                                <br />
                                {company.address.locality}
                                <br />
                                {company.address.country}
                            </address>

                            <PillLink href={company.mapsUrl} variant='outline-dark' size='sm' className='mt-6'>
                                Como chegar
                            </PillLink>

                            <dl className='mt-10 space-y-6 border-t border-ink/10 pt-8'>
                                {phonesByCity.map(({ city, lines }) => (
                                    <div key={city}>
                                        <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                            {city}
                                        </dt>

                                        {lines.map((line) => (
                                            <dd key={line.href} className='mt-2'>
                                                <a href={line.href} className='text-lg transition-colors hover:text-brand'>
                                                    {line.label}
                                                </a>
                                            </dd>
                                        ))}
                                    </div>
                                ))}

                                <div>
                                    <dt className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                        WhatsApp
                                    </dt>
                                    <dd className='mt-2'>
                                        <a
                                            href={company.whatsappUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='text-lg transition-colors hover:text-brand'
                                        >
                                            (21) 3907-7544
                                        </a>
                                    </dd>
                                </div>
                            </dl>

                            <div className='mt-10 border-t border-ink/10 pt-8'>
                                <h2 className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                    Já é cliente?
                                </h2>

                                <PillLink href={company.clientAreaUrl} variant='dark' size='sm' className='mt-4'>
                                    {clientAreaLabel}
                                </PillLink>
                            </div>
                        </div>
                    </aside>
                </Container>
            </section>
        </Layout>
    )
}

export default Contact
